/**
 * Aggregates the master-service activity log for one (course, student) into
 * per-chapter reading-session scores. The same shape we'd otherwise fetch from
 * activity-analyser's /v1/api/scoring/students/{id}/chapters, computed
 * client-side from the source-of-truth endpoint already wired in the app:
 *
 *   GET /v1/api/master/assignmentProgress/activity/{courseId}/{studentId}
 *
 * Algorithm:
 *  - Group activity events by chapterId.
 *  - Walk events in creation order (NOT sorted — see note below).
 *  - Every CHAPTER_COMPLETE closes one attempt window. SPEAK_ALOUD sentences
 *    recorded AFTER the last CHAPTER_COMPLETE merge back into it (write-ordering
 *    race), so a finished session reads as one attempt over all its sentences.
 *  - For each attempt, average the readingScore across every speakAloudPayload
 *    sentence collected since the previous CHAPTER_COMPLETE (or start).
 *  - Independently, derive a "needed support" signal from per-sentence retry
 *    counts + INCORRECT_SPEAK_ALOUD give-ups. This pass NEVER touches the average.
 */
const toNum = (v) => {
    if (v == null) return NaN;
    if (typeof v === 'number') return v;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
};

const round1 = (n) => Math.round(n * 10) / 10;

// --- Needed-support tuning -------------------------------------------------
// A sentence "struggled" when it took >= STRUGGLE_RETRY_THRESHOLD incorrect
// attempts (covers "2 fails then pass" and give-ups). A session needs support
// once STRUGGLE_SENTENCE_THRESHOLD (3) or more of its sentences struggled — an
// absolute count. These thresholds are read-side only and never affect any score.
const MAX_RETRIES = 3; // tries before a sentence is "given up"
const STRUGGLE_RETRY_THRESHOLD = 2; // incorrect attempts that mark a sentence struggled
// Exported so Reports.jsx rolls the chapter/course flags up against the same number.
export const STRUGGLE_SENTENCE_THRESHOLD = 3; // >= this many struggled sentences -> flag

/**
 * Accrue per-sentence retry stats from a SPEAK_ALOUD payload array into `acc`
 * ({ sentences, struggled, reads }). A missing retryCount is treated as 0 (a
 * clean first-try pass), so historical data without the field degrades cleanly.
 */
function accrueStruggleSentences(acc, sentences) {
    if (!Array.isArray(sentences)) return;
    for (const s of sentences) {
        const rc = toNum(s && s.retryCount);
        const retry = Number.isNaN(rc) ? 0 : Math.max(0, rc);
        acc.sentences += 1;
        // reads = wrong tries + the accepted read, capped at MAX_RETRIES: a retryCount of
        // MAX_RETRIES means every try was used and the student moved on (no successful final
        // read), so it must not count as MAX_RETRIES + 1.
        acc.reads += Math.min(retry + 1, MAX_RETRIES);
        if (retry >= STRUGGLE_RETRY_THRESHOLD) acc.struggled += 1;
    }
}

/**
 * Drop sentences whose `audio` blob was already counted anywhere in this log. The
 * capture sometimes re-stamps a sentence's payload onto later events — the previous
 * chapter's last read leaking onto the next chapter's first SPEAK_ALOUD, or echoed
 * onto a CHAPTER_COMPLETE / ASSESSMENT event. Each audio blob is one real recording,
 * so counting it once removes those phantom duplicates. Empty audio is never deduped
 * (there's no key to match on).
 */
function dedupeByAudio(payload, seenAudio) {
    if (!Array.isArray(payload)) return [];
    const out = [];
    for (const s of payload) {
        const audio = s && s.audio;
        if (audio && seenAudio.has(audio)) continue;
        if (audio) seenAudio.add(audio);
        out.push(s);
    }
    return out;
}

export function aggregateChapterScores(activities) {
    if (!Array.isArray(activities) || activities.length === 0) return [];

    // Pass 1 — dedupe by audio blob in EVENT order, before any grouping. The capture
    // re-stamps a sentence's payload onto later events (the previous chapter's last read
    // leaking into the next chapter's first SPEAK_ALOUD, or echoed onto CHAPTER_COMPLETE /
    // ASSESSMENT). The FIRST occurrence (earliest event) is the real read; later ones are
    // duplicates. This MUST run in event order: the grouping below is by chapterId, so an
    // echo in an earlier-listed chapter would otherwise claim a sentence that belongs to a
    // later-listed chapter (and steal it from the real read).
    const seenAudio = new Set();
    const cleaned = activities.map((ev) =>
        ev && Array.isArray(ev.speakAloudPayload)
            ? { ...ev, speakAloudPayload: dedupeByAudio(ev.speakAloudPayload, seenAudio) }
            : ev
    );

    // Group by chapterId, preserving chapter metadata seen on any event.
    const byChapter = new Map();
    for (const ev of cleaned) {
        if (!ev || !ev.chapterId) continue;
        if (!byChapter.has(ev.chapterId)) byChapter.set(ev.chapterId, []);
        byChapter.get(ev.chapterId).push(ev);
    }

    const chapters = [];
    for (const [chapterId, evs] of byChapter.entries()) {
        // DO NOT sort by endTime. In the master-service response every SPEAK_ALOUD
        // event in a session shares the same endTime (the session's end time), while
        // the CHAPTER_COMPLETE that closes the attempt has a slightly later endTime.
        // Re-sorting would push CHAPTER_COMPLETE to the end of the list and merge
        // a retake's sentences into one giant first attempt. The API already returns
        // events in creation order — trust that.

        let chapterName = null;
        let sectionId = null;
        let sectionName = null;

        // Bucket sentences into reading sessions ("attempts") by their shared
        // (startTime|endTime) window. Every SPEAK_ALOUD event in one session carries
        // identical start/end times; the CHAPTER_COMPLETE that closes it has a slightly
        // different (later) time. Keying off the SPEAK_ALOUD window — instead of each
        // CHAPTER_COMPLETE's position in the event array — is what keeps a CC that was
        // written mid-session (a write-ordering race) from splitting that session's
        // sentences into two phantom attempts.
        const windows = new Map();
        const windowFor = (ev) => {
            const key = `${ev.startTime || ''}|${ev.endTime || ''}`;
            let w = windows.get(key);
            if (!w) {
                w = {
                    startTime: ev.startTime || null,
                    endTime: ev.endTime || null,
                    sentences: [],
                    // Needed-support accumulator, per session window. NEVER feeds any average.
                    struggle: { sentences: 0, struggled: 0, reads: 0 },
                };
                windows.set(key, w);
            }
            return w;
        };

        for (const ev of evs) {
            if (!chapterName && ev.chapterName) chapterName = ev.chapterName;
            if (!sectionId && ev.sectionId) sectionId = ev.sectionId;
            if (!sectionName && ev.sectionName) sectionName = ev.sectionName;

            if (ev.type === 'SPEAK_ALOUD') {
                if (Array.isArray(ev.speakAloudPayload) && ev.speakAloudPayload.length > 0) {
                    const w = windowFor(ev);
                    w.sentences.push(...ev.speakAloudPayload);
                    accrueStruggleSentences(w.struggle, ev.speakAloudPayload);
                }
            } else if (ev.type === 'INCORRECT_SPEAK_ALOUD') {
                // A given-up sentence (failed all MAX_RETRIES tries). Counts as one
                // struggled sentence for its session's support signal, but carries no
                // score so it never enters a reading average.
                const w = windowFor(ev);
                w.struggle.sentences += 1;
                w.struggle.struggled += 1;
                w.struggle.reads += MAX_RETRIES;
            } else if (ev.type === 'CHAPTER_COMPLETE') {
                // Salvage a CC-carried payload only for real recordings (non-empty
                // audio) — the production "Session 2" case where the only graded
                // sentence rides the CC event. Echoes of SPEAK_ALOUD reads were already
                // removed by the global audio-dedupe pass, so this won't double-count.
                if (Array.isArray(ev.speakAloudPayload) && ev.speakAloudPayload.length > 0) {
                    const real = ev.speakAloudPayload.filter((s) => s && s.audio);
                    if (real.length > 0) {
                        const w = windowFor(ev);
                        w.sentences.push(...real);
                        accrueStruggleSentences(w.struggle, real);
                    }
                }
            }
        }

        // Each window is one attempt, ordered chronologically by session start.
        const ordered = [...windows.values()].sort((a, b) =>
            String(a.startTime || '').localeCompare(String(b.startTime || ''))
        );
        const attempts = ordered.map((w, i) => {
            const scores = w.sentences
                .map((s) => toNum(s && s.readingScore))
                .filter((n) => !Number.isNaN(n));
            const sum = scores.reduce((a, b) => a + b, 0);
            return {
                attemptNumber: i + 1,
                averageReadingScore: scores.length ? round1(sum / scores.length) : 0,
                sentenceCount: scores.length,
                startedAt: w.startTime,
                finishedAt: w.endTime,
                struggleStats: w.struggle,
                // Trimmed per-sentence list for the modal's expandable view. Only
                // text + score — the heavy audio/outcome blobs are dropped. Key is
                // `sentance` (a misspelling baked into the source payload).
                sentences: w.sentences.map((s) => {
                    const v = toNum(s && s.readingScore);
                    return {
                        text: (s && (s.sentance ?? s.sentence)) || '',
                        score: Number.isFinite(v) ? v : null,
                    };
                }),
            };
        });

        if (attempts.length === 0) continue;
        // Skip chapters that have no graded sentences in any attempt
        // (e.g. STORY_READING-only chapters where every readingScore is "0").
        const totalScoredSentences = attempts.reduce((n, a) => n + (a.sentenceCount || 0), 0);
        if (totalScoredSentences === 0) continue;

        const best = attempts.reduce(
            (acc, a) => (a.averageReadingScore > acc.averageReadingScore ? a : acc),
            attempts[0]
        );
        const latest = attempts[attempts.length - 1];
        const earliest = attempts[0];

        // Tag `best` on whichever row carries the max (ties → earliest wins).
        // Strip the internal struggleStats so the exposed attempt rows stay clean.
        let bestTagged = false;
        const taggedAttempts = attempts.map((a) => {
            const isBest = !bestTagged && a.averageReadingScore === best.averageReadingScore;
            if (isBest) bestTagged = true;
            const { struggleStats, ...rest } = a;
            return { ...rest, best: isBest };
        });

        // Needed-support signal for this session (chapter). Read-side only — derived
        // from retry counts, never from scores. Scoped to the LATEST attempt so it
        // agrees with the latest-attempt sentence count below. Flag at 3+ struggled.
        const latestStruggle = latest.struggleStats || { sentences: 0, struggled: 0, reads: 0 };
        const needsSupport = latestStruggle.struggled >= STRUGGLE_SENTENCE_THRESHOLD;

        chapters.push({
            chapterId,
            chapterName,
            sectionId,
            sectionName,
            bestAverage: best.averageReadingScore,
            latestAverage: latest.averageReadingScore,
            attemptCount: attempts.length,
            totalSentences: latest.sentenceCount,
            firstAttemptAt: earliest.finishedAt || earliest.startedAt,
            lastAttemptAt: latest.finishedAt || latest.startedAt,
            attempts: taggedAttempts,
            // Needed-support stats for the latest attempt (do NOT affect any score).
            needsSupport,
            struggledSentences: latestStruggle.struggled,
            sentencesAttempted: latestStruggle.sentences,
            totalReads: latestStruggle.reads,
        });
    }

    // Most-recently-touched chapter first.
    chapters.sort((a, b) =>
        String(b.lastAttemptAt || '').localeCompare(String(a.lastAttemptAt || ''))
    );
    return chapters;
}
